import { useAuth } from '@/lib'
import { logger, TimeAgo, Tooltip } from '@/ui'
import type { WhatsAppMessageStatus } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import {
	AlertTriangleIcon,
	CheckCheckIcon,
	CheckIcon,
	ClockIcon,
	EyeIcon,
	FileIcon,
	ImageIcon,
	Loader2Icon,
	ZapIcon,
} from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { WhatsAppMediaUrl, WhatsAppMessage } from './types'

const MEDIA_GONE_STATUS = 410

interface MediaAttachmentProps {
	media: WhatsAppMediaUrl
	isFromCandidate: boolean
}

function MediaAttachment({ media, isFromCandidate }: MediaAttachmentProps) {
	const { getAccessToken } = useAuth()
	const [blobUrl, setBlobUrl] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState(false)
	const blobUrlRef = useRef<string | null>(null)

	const contentType = media.content_type.toLowerCase()
	const isImage = contentType.startsWith('image/')
	const isVideo = contentType.startsWith('video/')
	const isAudio = contentType.startsWith('audio/')

	useEffect(() => {
		let mounted = true
		const controller = new AbortController()

		async function fetchMedia() {
			try {
				const token = await getAccessToken()
				const response = await fetch(`${import.meta.env.VITE_NEXUS_API_URL}/${media.url}`, {
					headers: {
						'Authorization': token ? `Bearer ${token}` : '',
					},
					signal: controller.signal,
				})

				if (!response.ok) {
					if (mounted) {
						const context = {
							tags: { component: 'WhatsAppItem', action: 'loadMedia' },
							data: { status: response.status, mediaUrl: media.url },
						}

						if (response.status === MEDIA_GONE_STATUS) {
							logger.warn('WhatsApp media no longer available', context)
						} else {
							logger.error(new Error(`Failed to fetch media (${response.status})`), context)
						}

						setError(true)
						setIsLoading(false)
					}

					return
				}

				const blob = await response.blob()
				if (mounted) {
					const url = URL.createObjectURL(blob)
					blobUrlRef.current = url
					setBlobUrl(url)
					setIsLoading(false)
				}
			} catch (err) {
				if (mounted && err instanceof Error && err.name !== 'AbortError') {
					logger.error(err, {
						tags: { component: 'WhatsAppItem', action: 'loadMedia' },
					})
					setError(true)
					setIsLoading(false)
				}
			}
		}

		void fetchMedia()

		return () => {
			mounted = false
			controller.abort()
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current)
				blobUrlRef.current = null
			}
		}
	}, [media.url, getAccessToken])

	if (isLoading) {
		return (
			<div
				className={`flex items-center justify-center w-32 h-32 rounded-lg ${
					isFromCandidate ? 'bg-gray-200' : 'bg-purple-700'
				}`}
			>
				<Loader2Icon className={`size-6 animate-spin ${isFromCandidate ? 'text-gray-500' : 'text-white/70'}`} />
			</div>
		)
	}

	if (error || !blobUrl) {
		return (
			<div
				className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
					isFromCandidate ? 'bg-gray-200 text-gray-500' : 'bg-purple-700 text-white/70'
				}`}
			>
				<ImageIcon className="size-4" />
				<span className="text-sm">Failed to load media</span>
			</div>
		)
	}

	if (isImage) {
		return (
			<a href={blobUrl} target="_blank" rel="noopener noreferrer" className="block">
				<img
					src={blobUrl}
					alt="Attachment"
					className="max-w-full rounded-lg max-h-64 object-contain"
				/>
			</a>
		)
	}

	if (isVideo) {
		return (
			<video
				src={blobUrl}
				controls
				className="max-w-full rounded-lg max-h-64"
			>
				Your browser does not support the video tag.
			</video>
		)
	}

	if (isAudio) {
		return (
			<audio src={blobUrl} controls className="w-full">
				Your browser does not support the audio element.
			</audio>
		)
	}

	// Generic file attachment
	return (
		<a
			href={blobUrl}
			download
			className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
				isFromCandidate ? 'bg-gray-200 hover:bg-gray-300' : 'bg-purple-700 hover:bg-purple-800'
			}`}
		>
			<FileIcon className="size-4" />
			<span className="text-sm">Download attachment</span>
		</a>
	)
}

function getStatusIcon(status: WhatsAppMessageStatus, isFromCandidate: boolean) {
	if (isFromCandidate) {
		return null
	}

	switch (status) {
		case 'pending':
			return <ClockIcon className="size-3.5 text-gray-400" />
		case 'sent':
			return <CheckIcon className="size-3.5 text-gray-400" />
		case 'delivered':
			return <CheckCheckIcon className="size-3.5 text-gray-400" />
		case 'read':
			return <CheckCheckIcon className="size-3.5 text-blue-500" />
		case 'failed':
			return <AlertTriangleIcon className="size-3.5 text-red-500" />
		default:
			return null
	}
}

function getStatusTooltip(status: WhatsAppMessageStatus): string {
	switch (status) {
		case 'pending':
			return 'Sending...'
		case 'sent':
			return 'Sent'
		case 'delivered':
			return 'Delivered'
		case 'read':
			return 'Read by recipient'
		case 'failed':
			return 'Failed to send'
		default:
			return ''
	}
}

export interface WhatsAppItemProps {
	message: WhatsAppMessage
	onClick?: (messageId: string) => void
}

export const WhatsAppItem = memo(function WhatsAppItem({
	message,
	onClick,
}: WhatsAppItemProps) {
	const handleClick = useCallback(() => {
		onClick?.(message.id)
	}, [onClick, message.id])

	const containerClassName = useMemo(
		() =>
			ctl(`
		px-4 py-2 cursor-pointer transition-colors
		${!message.isRead && message.isFromCandidate ? 'bg-blue-50/50' : ''}
	`),
		[message.isRead, message.isFromCandidate],
	)

	const bubbleClassName = useMemo(
		() =>
			ctl(`
		max-w-[85%] rounded-lg px-3 py-2 inline-block
		${
				message.isFromCandidate
					? 'bg-gray-100 text-gray-900'
					: 'bg-purple-600 text-white'
			}
		${message.status === 'failed' ? 'bg-red-100 text-red-900' : ''}
	`),
		[message.isFromCandidate, message.status],
	)

	const statusIcon = getStatusIcon(message.status, message.isFromCandidate)
	const statusTooltip = getStatusTooltip(message.status)
	const messageTime = message.sentAt ?? message.createdAt

	return (
		<div className={containerClassName} onClick={handleClick}>
			<div className={`flex ${message.isFromCandidate ? 'justify-start' : 'justify-end'}`}>
				<div className={bubbleClassName}>
					{/* Unread indicator for inbound messages */}
					{!message.isRead && message.isFromCandidate && (
						<div className="flex items-center gap-1.5 mb-1">
							<span className="size-2 rounded-full bg-blue-500 flex-shrink-0" />
							<span className="text-xs text-blue-600 font-medium">New</span>
						</div>
					)}

					{/* Template name badge for outbound messages */}
					{!message.isFromCandidate && message.templateName && (
						<div className="text-xs opacity-75 mb-1">
							Template: {message.templateName}
						</div>
					)}

					{!message.isFromCandidate && message.isAutomated && (
						<div className="text-xs opacity-75 mb-1 flex items-center gap-1">
							<ZapIcon className="size-3" />
							Sent automatically
						</div>
					)}

					{/* Media attachments */}
					{message.mediaUrls && message.mediaUrls.length > 0 && (
						<div className="mb-2 space-y-2">
							{message.mediaUrls.map((media, index) => (
								<MediaAttachment key={index} media={media} isFromCandidate={message.isFromCandidate} />
							))}
						</div>
					)}

					{/* Message content */}
					{message.content && (
						<div className="text-sm whitespace-pre-wrap break-words">
							{message.content}
						</div>
					)}

					{/* Error message */}
					{message.status === 'failed' && message.errorMessage && (
						<div className="mt-2 text-xs text-red-600 flex items-center gap-1">
							<AlertTriangleIcon className="size-3" />
							{message.errorMessage}
						</div>
					)}

					{/* Footer with time and status */}
					<div
						className={`flex items-center gap-1.5 mt-1 text-xs ${
							message.isFromCandidate ? 'text-gray-500' : 'text-white/70'
						}`}
					>
						<TimeAgo date={messageTime} short />

						{statusIcon && (
							<Tooltip content={statusTooltip}>
								<span className="flex items-center">
									{statusIcon}
								</span>
							</Tooltip>
						)}

						{/* Read by team indicator for inbound messages */}
						{message.isFromCandidate && message.readBy.length > 0 && (
							<Tooltip
								content={`Seen by ${message.readBy.length} team member${
									message.readBy.length > 1 ? 's' : ''
								}`}
							>
								<div className="flex items-center gap-0.5 text-gray-400">
									<EyeIcon className="size-3" />
									<span>{message.readBy.length}</span>
								</div>
							</Tooltip>
						)}
					</div>
				</div>
			</div>
		</div>
	)
})
